/* eslint-disable @typescript-eslint/no-explicit-any */
import { createInstance, createPersonalInstance } from './instance';

import {
  BACKEND_URL,
  ACCESS_TOKEN_KEY,
  REFRESH_TOKEN_KEY,
  PARK_BACKEND_URL,
} from '@/constants';
import { FETCH_METHODS } from '@/types';
import axios, { AxiosError } from 'axios';
import { TokensType } from '@/types';
import { logoutDreamSDK } from '@/utils/dreamCRM';

export const fetchData = async ({
  url,
  method = FETCH_METHODS.GET,
  body,
}: {
  url: string;
  method?: FETCH_METHODS;
  body?: any;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axiosInstance.request({
      url: `${BACKEND_URL}/api/app${url}`,
      method,
      data: body,
    });

    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchParkData = async ({
  url,
  method = FETCH_METHODS.GET,
  body,
}: {
  url: string;
  method?: FETCH_METHODS;
  body?: any;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axiosInstance.request({
      url: `${PARK_BACKEND_URL}/api/app${url}`,
      method,
      data: body,
    });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchSecurityData = async ({
  url,
  method = FETCH_METHODS.GET,
  body,
}: {
  url: string;
  method?: FETCH_METHODS;
  body?: any;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axiosInstance.request({
      url: `${BACKEND_URL}/api/security${url}`,
      method,
      data: body,
    });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchPersonalData = async ({
  url,
  method = FETCH_METHODS.GET,
  body,
}: {
  url: string;
  method?: FETCH_METHODS;
  body?: any;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await personalAxios.request({
      url: `${BACKEND_URL}/api/app/personal${url}`,
      method,
      data: body,
    });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchFormData = async ({
  url,
  method = FETCH_METHODS.POST,
  body,
  headers,
}: {
  url: string;
  method?: FETCH_METHODS;
  body?: any;
  headers?: Record<string, string>;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axiosInstance.request({
      url: `${BACKEND_URL}/api/app${url}`,
      method,
      data: body,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...headers,
      },
    });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchDadataAddress = async ({
  query,
}: {
  query: string;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axios.post('/api/dadata/suggest', { query });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const fetchDadataAddressByCoordinates = async ({
  lat,
  lon,
}: {
  lat: number;
  lon: number;
}): Promise<{
  success: boolean;
  data: any | null;
  error: AxiosError | null;
}> => {
  try {
    const { data } = await axios.post('/api/dadata/geolocate', { lat, lon });
    return { success: true, data, error: null };
  } catch (error) {
    return { success: false, data: null, error: error as AxiosError };
  }
};

export const logout = async () => {
  removeTokens();
  window.location.href = '/';
};

export const getTokens = (): TokensType => {
  if (typeof window === 'undefined') {
    return {
      accessToken: undefined,
      refreshToken: undefined,
    };
  }

  return {
    accessToken: localStorage.getItem(ACCESS_TOKEN_KEY) || undefined,
    refreshToken: localStorage.getItem(REFRESH_TOKEN_KEY) || undefined,
  };
};

export const setTokens = (access: string, refresh: string): void => {
  if (access) {
    localStorage.setItem(ACCESS_TOKEN_KEY, access);
  }
  if (refresh) {
    localStorage.setItem(REFRESH_TOKEN_KEY, refresh);
  }
};

export const removeTokens = (): void => {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(REFRESH_TOKEN_KEY);
  logoutDreamSDK();
};

export const SECURITY_ENDPOINTS = {
  LOGIN: `${BACKEND_URL}/api/security/login`,
  REFRESH: `${BACKEND_URL}/api/security/refresh`,
} as const;

export const axiosInstance = createInstance({
  getTokens: getTokens,
});

export const personalAxios = createPersonalInstance({
  getTokens: getTokens,
  setTokens: setTokens,
  removeTokens: removeTokens,
  refreshEndpoint: SECURITY_ENDPOINTS.REFRESH,
});
